import React, { useState, useEffect, useRef } from 'react';
import API from '../api';
import { Brain, Upload, BookOpen, Clock, Plus, X, Trash2, FileText, ChevronLeft, ChevronRight, Lightbulb, Database, ScrollText, Tag, Check } from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';

const tipoLabels = { correzione_utente: 'Correzione', match_confermato: 'Match', pattern_prezzo: 'Prezzo', esempio_storico: 'Esempio', regola_aziendale: 'Regola', voce_lavoro: 'Voce', pattern: 'Pattern', margine: 'Margine', esclusione: 'Esclusione', nota_tecnica: 'Nota' };
const tipoColors = { correzione_utente: 'bg-yellow-100 text-yellow-800', match_confermato: 'bg-green-100 text-green-700', pattern_prezzo: 'bg-blue-100 text-blue-700', esempio_storico: 'bg-purple-100 text-purple-700', regola_aziendale: 'bg-primary text-primary' };

export default function FormazioneAIPage() {
  const confirm = useConfirm();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [knowledge, setKnowledge] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [journal, setJournal] = useState({ items: [], total: 0 });
  const [training, setTraining] = useState([]);
  const [kPage, setKPage] = useState(1);
  const [showAddRule, setShowAddRule] = useState(false);
  const [ruleForm, setRuleForm] = useState({ lezione: '', contesto: '', tags: '' });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadStats = () => API.get('/api/ai/knowledge/stats').then(r => setStats(r.data)).catch(() => {});
  const loadKnowledge = (p) => API.get('/api/ai/knowledge', { params: { page: p || kPage, limit: 20 } }).then(r => setKnowledge(r.data)).catch(() => {});
  const loadJournal = () => API.get('/api/ai/journal', { params: { limit: 30 } }).then(r => setJournal(r.data)).catch(() => {});
  const loadTraining = () => API.get('/api/ai/training').then(r => setTraining(r.data)).catch(() => {});

  useEffect(() => { loadStats(); loadKnowledge(1); loadJournal(); loadTraining(); }, []);
  useEffect(() => { loadKnowledge(kPage); }, [kPage]);

  const addRule = async () => {
    await API.post('/api/ai/knowledge', { tipo: 'regola_aziendale', lezione: ruleForm.lezione, contesto: ruleForm.contesto, tags: ruleForm.tags.split(',').map(t => t.trim()).filter(Boolean) });
    setShowAddRule(false);
    setRuleForm({ lezione: '', contesto: '', tags: '' });
    loadStats();
    loadKnowledge(kPage);
    loadJournal();
  };

  const deleteKnowledge = async (id) => {
    if (await confirm({ title: 'Eliminare conoscenza?', message: 'L\'AI non userà più questa regola/esempio nelle future generazioni.', confirmLabel: 'Elimina', variant: 'danger' })) {
      await API.delete(`/api/ai/knowledge/${id}`);
      loadStats();
      loadKnowledge(kPage);
    }
  };

  const uploadTraining = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipo_documento', file.name.toLowerCase().includes('preventiv') ? 'preventivo' : 'computo');
    try {
      await API.post('/api/ai/training/upload', formData);
      loadTraining();
      loadStats();
      loadKnowledge(1);
      loadJournal();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const deleteTraining = async (id) => {
    if (await confirm({ title: 'Eliminare documento e conoscenze estratte?', message: 'Il documento di training e tutte le conoscenze che ne sono state estratte verranno rimossi.', confirmLabel: 'Elimina tutto', variant: 'danger' })) {
      await API.delete(`/api/ai/training/${id}`);
      loadTraining();
      loadStats();
      loadKnowledge(kPage);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('it-IT');

  return (
    <div className="animate-fade-in" data-testid="formazione-ai-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <Brain size={28} className="text-primary" /> Formazione AI
          </h1>
          <p className="text-slate-500 mt-1">Il sistema AI impara dalla tua attivita e dai tuoi documenti</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={() => setShowAddRule(true)} data-testid="add-rule-btn"
            className="px-3 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95 flex items-center gap-1.5 text-sm">
            <Plus size={16} /> Regola
          </button>
          <input type="file" ref={fileRef} className="hidden" accept=".pdf,.docx,.doc,.xls,.xlsx,.txt" onChange={uploadTraining} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-training-btn"
            className="px-3 py-2 bg-accent-info text-white font-bold rounded-md hover:bg-blue-700 transition-all active:scale-95 flex items-center gap-1.5 text-sm disabled:opacity-50">
            <Upload size={16} /> {uploading ? 'Analisi...' : 'Documento'}
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <StatCard icon={Database} label="Conoscenze" value={stats.totale} color="text-primary" />
          <StatCard icon={Lightbulb} label="Correzioni" value={stats.correzioni} color="text-yellow-600" />
          <StatCard icon={Tag} label="Match" value={stats.match_confermati} color="text-green-600" />
          <StatCard icon={BookOpen} label="Esempi" value={stats.esempi_storici} color="text-purple-600" />
          <StatCard icon={ScrollText} label="Regole" value={stats.regole_aziendali} color="text-primary" />
          <StatCard icon={Clock} label="Journal" value={stats.journal_entries} color="text-blue-600" />
          <StatCard icon={FileText} label="Documenti" value={stats.documenti_training} color="text-indigo-600" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-white border border-slate-200 rounded-md p-1 overflow-x-auto">
        {[{id:'overview',label:'Conoscenza'},{id:'journal',label:'Diario AI'},{id:'training',label:'Training'},{id:'assistant',label:'Assistente Geppo'}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-4 py-2 rounded text-sm font-bold transition-all whitespace-nowrap ${tab === t.id ? 'bg-surface-dark text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'assistant' && <AssistantConfig />}

      {/* Knowledge Base Tab */}
      {tab === 'overview' && (
        <div>
          <div className="space-y-2" data-testid="knowledge-list">
            {knowledge.items?.map(k => (
              <div key={k.id} className="bg-white border border-slate-200 rounded-md p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${tipoColors[k.tipo] || 'bg-slate-100 text-slate-700'}`}>
                      {tipoLabels[k.tipo] || k.tipo}
                    </span>
                    {k.peso > 1 && <span className="text-[10px] font-mono text-slate-400">x{k.peso}</span>}
                    {k.tags?.map(t => <span key={t} className="text-[10px] bg-slate-50 px-1.5 py-0.5 rounded text-slate-400">{t}</span>)}
                  </div>
                  <p className="text-sm">{k.lezione}</p>
                  {k.contesto && <p className="text-xs text-slate-400 mt-1">{k.contesto}</p>}
                </div>
                <button onClick={() => deleteKnowledge(k.id)} className="p-1 hover:bg-red-50 rounded flex-shrink-0">
                  <Trash2 size={14} className="text-red-400" />
                </button>
              </div>
            ))}
            {knowledge.items?.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <Brain size={48} className="mx-auto mb-3 text-slate-300" />
                <p className="font-semibold">Nessuna conoscenza ancora</p>
                <p className="text-sm mt-1">Il sistema impara automaticamente quando usi le funzionalita AI, validi prezzi, o carichi documenti storici</p>
              </div>
            )}
          </div>
          {knowledge.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button onClick={() => setKPage(p => Math.max(1, p - 1))} disabled={kPage <= 1} className="p-2 border rounded-md disabled:opacity-30"><ChevronLeft size={16} /></button>
              <span className="text-sm text-slate-500">Pag. {kPage} di {knowledge.pages}</span>
              <button onClick={() => setKPage(p => Math.min(knowledge.pages, p + 1))} disabled={kPage >= knowledge.pages} className="p-2 border rounded-md disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          )}
        </div>
      )}

      {/* Journal Tab */}
      {tab === 'journal' && (
        <div className="space-y-2" data-testid="journal-list">
          {journal.items?.map(j => (
            <div key={j.id} className="bg-white border border-slate-200 rounded-md p-3">
              <div className="flex items-center gap-2 mb-1">
                <Clock size={14} className="text-slate-400" />
                <span className="text-xs text-slate-400">{j.created_at ? new Date(j.created_at).toLocaleString('it-IT') : ''}</span>
                <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold text-slate-600">{j.azione}</span>
              </div>
              <p className="text-sm">{j.dettagli}</p>
              {j.appreso && <p className="text-xs text-green-600 mt-1 font-medium">Appreso: {j.appreso}</p>}
            </div>
          ))}
          {journal.items?.length === 0 && <p className="text-center py-8 text-slate-400">Il diario AI e vuoto. Inizia a usare le funzionalita AI!</p>}
        </div>
      )}

      {/* Training Docs Tab */}
      {tab === 'training' && (
        <div>
          <p className="text-sm text-slate-500 mb-4">Carica computi metrici e preventivi reali gia realizzati dalla tua azienda. L'AI li analizzera ed estrarra conoscenze utili per futuri lavori.</p>
          <div className="space-y-2" data-testid="training-list">
            {training.map(t => (
              <div key={t.id} className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={16} className="text-primary" />
                    <h3 className="font-bold text-sm">{t.filename}</h3>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${t.stato === 'analizzato' ? 'bg-green-100 text-green-700' : t.stato === 'caricato' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-700'}`}>
                      {t.stato === 'analizzato' ? 'Analizzato' : t.stato === 'caricato' ? 'In Analisi' : 'Errore'}
                    </span>
                    <span className="text-[10px] bg-blue-50 px-1.5 py-0.5 rounded text-blue-600">{t.tipo_documento}</span>
                  </div>
                  <p className="text-xs text-slate-400">
                    {t.caratteri ? `${(t.caratteri / 1000).toFixed(1)}k caratteri` : ''} 
                    {t.conoscenze_estratte ? ` - ${t.conoscenze_estratte} conoscenze estratte` : ''}
                    {t.created_at ? ` - ${new Date(t.created_at).toLocaleDateString('it-IT')}` : ''}
                  </p>
                </div>
                <button onClick={() => deleteTraining(t.id)} className="p-2 hover:bg-red-50 rounded">
                  <Trash2 size={16} className="text-red-400" />
                </button>
              </div>
            ))}
            {training.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <Upload size={48} className="mx-auto mb-3 text-slate-300" />
                <p className="font-semibold">Nessun documento di training</p>
                <p className="text-sm mt-1">Carica preventivi e computi metrici reali per addestrare l'AI</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Rule Modal */}
      {showAddRule && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-lg max-h-[92vh] sm:max-h-[85vh] overflow-y-auto p-5 sm:p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Aggiungi Regola Aziendale</h3>
              <button onClick={() => setShowAddRule(false)}><X size={20} className="text-slate-400" /></button>
            </div>
            <p className="text-sm text-slate-500 mb-3">Aggiungi una regola che l'AI seguira in futuro quando genera computi e preventivi.</p>
            <div className="space-y-3">
              <textarea data-testid="rule-text" placeholder="Es: Per ristrutturazioni bagno, includi sempre impermeabilizzazione e ventilazione forzata. Il nostro margine standard e del 25% sul listino." rows={4}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-primary/50"
                value={ruleForm.lezione} onChange={e => setRuleForm({...ruleForm, lezione: e.target.value})} />
              <input placeholder="Contesto (opzionale)" className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm"
                value={ruleForm.contesto} onChange={e => setRuleForm({...ruleForm, contesto: e.target.value})} />
              <input placeholder="Tags (separati da virgola, es: bagno, ristrutturazione)" className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm"
                value={ruleForm.tags} onChange={e => setRuleForm({...ruleForm, tags: e.target.value})} />
              <button onClick={addRule} disabled={!ruleForm.lezione.trim()} data-testid="save-rule-btn"
                className="w-full py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95 disabled:opacity-50">
                Salva Regola
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} className={color} />
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-black">{value || 0}</p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ASSISTENTE GEPPO - Configurazione
// ══════════════════════════════════════════════════════════════
function AssistantConfig() {
  const [subtab, setSubtab] = useState('skills');
  const [config, setConfig] = useState(null);
  const [skills, setSkills] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    const [cfgR, sk] = await Promise.all([
      API.get('/api/assistant/config'),
      API.get('/api/assistant/skills'),
    ]);
    setConfig(cfgR.data);
    setSkills(sk.data);
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      const next = { ...config, ...patch };
      const r = await API.put('/api/assistant/config', next);
      setConfig(r.data);
      setMsg('Salvato!');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const toggleSkill = async (id) => {
    try {
      await API.put(`/api/assistant/skills/${id}/toggle`);
      load();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };

  if (!config) return <div className="text-slate-400 py-8 text-center">Caricamento configurazione Geppo...</div>;

  const subtabs = [
    { id: 'skills', label: 'Skills attive' },
    { id: 'regole', label: 'Regole comportamento' },
    { id: 'azienda', label: 'Info azienda' },
    { id: 'personalita', label: 'Personalita e prompt' },
  ];

  return (
    <div className="space-y-4" data-testid="assistant-config">
      <div className="bg-primary border border-primary rounded-md p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-2xl">{config.emoji || '👷'}</div>
          <div>
            <p className="font-black text-lg">{config.nome_assistente || 'Geppo'}</p>
            <p className="text-xs text-slate-600">Capocantiere virtuale · Modello {config.modello_llm} · Voce {config.voce_modello}</p>
          </div>
        </div>
        {msg && <span className={`text-sm font-bold ${msg.startsWith('Errore') ? 'text-red-600' : 'text-green-600'}`}>{msg}</span>}
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {subtabs.map(s => (
          <button key={s.id} onClick={() => setSubtab(s.id)} data-testid={`subtab-${s.id}`}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 ${subtab === s.id ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {subtab === 'skills' && (
        <div className="space-y-2" data-testid="skills-list">
          <p className="text-sm text-slate-600 mb-2">Attiva/disattiva le abilita di Geppo. Le skill disattivate non saranno eseguibili.</p>
          {['anagrafica', 'pratiche', 'preventivi', 'contabilita', 'ricerca', 'report'].map(cat => {
            const group = skills.filter(s => s.categoria === cat);
            if (!group.length) return null;
            return (
              <div key={cat} className="bg-white border border-slate-200 rounded-md p-3">
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-2">{cat}</p>
                <div className="space-y-1.5">
                  {group.map(s => (
                    <div key={s.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`skill-${s.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold">{s.label}</p>
                        <p className="text-xs text-slate-500 truncate">{s.descrizione}</p>
                      </div>
                      <button onClick={() => toggleSkill(s.id)} data-testid={`toggle-skill-${s.id}`}
                        className={`px-3 py-1 rounded-full text-xs font-bold flex-shrink-0 ${s.attiva ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-500'}`}>
                        {s.attiva ? 'Attiva' : 'Off'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {subtab === 'regole' && (
        <RegoleEditor config={config} onSave={saveConfig} saving={saving} />
      )}

      {subtab === 'azienda' && (
        <AziendaEditor config={config} onSave={saveConfig} saving={saving} />
      )}

      {subtab === 'personalita' && (
        <PersonalitaEditor config={config} onSave={saveConfig} saving={saving} />
      )}
    </div>
  );
}

function RegoleEditor({ config, onSave, saving }) {
  const [regole, setRegole] = useState(config.regole || []);
  const [newRegola, setNewRegola] = useState('');

  const add = () => {
    if (!newRegola.trim()) return;
    setRegole([...regole, { id: `r${Date.now()}`, regola: newRegola.trim(), attiva: true }]);
    setNewRegola('');
  };
  const toggle = (i) => { const rr = [...regole]; rr[i].attiva = !rr[i].attiva; setRegole(rr); };
  const remove = (i) => setRegole(regole.filter((_, x) => x !== i));

  return (
    <div className="space-y-3" data-testid="regole-editor">
      <p className="text-sm text-slate-600">Regole di comportamento che Geppo seguira sempre. Puoi disattivarle singolarmente senza eliminarle.</p>
      <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2">
        {regole.map((r, i) => (
          <div key={r.id} className="flex items-start gap-2 group">
            <button onClick={() => toggle(i)} className={`mt-0.5 w-4 h-4 rounded border-2 flex-shrink-0 ${r.attiva ? 'bg-primary border-primary' : 'border-slate-300'}`}>
              {r.attiva && <Check size={10} className="text-white" />}
            </button>
            <p className={`flex-1 text-sm ${r.attiva ? '' : 'text-slate-400 line-through'}`}>{r.regola}</p>
            <button onClick={() => remove(i)} className="opacity-0 group-hover:opacity-100 text-red-500 hover:bg-red-50 p-1 rounded">
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input type="text" value={newRegola} onChange={(e) => setNewRegola(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="Nuova regola (es. 'Firma sempre come Geppo')" className="input flex-1" data-testid="new-regola-input" />
        <button onClick={add} className="bg-slate-100 hover:bg-slate-200 text-sm font-bold px-3 rounded" data-testid="add-regola"><Plus size={14} /></button>
      </div>
      <button onClick={() => onSave({ regole })} disabled={saving} data-testid="save-regole"
        className="bg-primary text-white text-sm font-bold px-4 py-2 rounded hover:bg-primary/90 disabled:opacity-50">
        {saving ? 'Salvataggio...' : 'Salva regole'}
      </button>
    </div>
  );
}

function AziendaEditor({ config, onSave, saving }) {
  const [az, setAz] = useState(config.azienda || {});
  const fields = [
    ['ragione_sociale', 'Ragione Sociale'], ['piva', 'P.IVA'], ['cf', 'Codice Fiscale'],
    ['sede', 'Sede (indirizzo)'], ['citta', 'Citta'], ['cap', 'CAP'],
    ['telefono', 'Telefono'], ['email', 'Email'], ['iban', 'IBAN'], ['referente', 'Referente'],
  ];
  return (
    <div className="space-y-3" data-testid="azienda-editor">
      <p className="text-sm text-slate-600">Info aziendali che Geppo conosce e iniettera nei documenti che genera.</p>
      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map(([k, l]) => (
          <div key={k}>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{l}</label>
            <input type="text" value={az[k] || ''} onChange={(e) => setAz({ ...az, [k]: e.target.value })} className="input w-full" data-testid={`az-${k}`} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Note / Contesto</label>
          <textarea value={az.note || ''} onChange={(e) => setAz({ ...az, note: e.target.value })} rows="3" className="input w-full" />
        </div>
      </div>
      <button onClick={() => onSave({ azienda: az })} disabled={saving} data-testid="save-azienda"
        className="bg-primary text-white text-sm font-bold px-4 py-2 rounded hover:bg-primary/90 disabled:opacity-50">
        {saving ? 'Salvataggio...' : 'Salva info azienda'}
      </button>
    </div>
  );
}

function PersonalitaEditor({ config, onSave, saving }) {
  const [nome, setNome] = useState(config.nome_assistente || 'Geppo');
  const [emoji, setEmoji] = useState(config.emoji || '👷');
  const [voce, setVoce] = useState(config.voce_modello || 'onyx');
  const [modello, setModello] = useState(config.modello_llm || 'gpt-5.2');
  const [personalita, setPersonalita] = useState(config.personalita || '');

  const voci = [
    { v: 'onyx', l: 'Onyx (maschile profonda)' },
    { v: 'echo', l: 'Echo (maschile giovanile)' },
    { v: 'fable', l: 'Fable (narrativa)' },
    { v: 'alloy', l: 'Alloy (neutra)' },
    { v: 'nova', l: 'Nova (femminile)' },
    { v: 'shimmer', l: 'Shimmer (femminile morbida)' },
    { v: 'sage', l: 'Sage (saggia)' },
    { v: 'coral', l: 'Coral (calda)' },
    { v: 'ash', l: 'Ash (asciutta)' },
  ];

  return (
    <div className="space-y-3" data-testid="personalita-editor">
      <p className="text-sm text-slate-600">Identita e system prompt del tuo assistente.</p>
      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nome</label>
            <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} className="input w-full" data-testid="pers-nome" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Emoji</label>
            <input type="text" value={emoji} onChange={(e) => setEmoji(e.target.value)} className="input w-full text-xl text-center" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Voce</label>
            <select value={voce} onChange={(e) => setVoce(e.target.value)} className="input w-full">
              {voci.map(v => <option key={v.v} value={v.v}>{v.l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Modello LLM</label>
            <select value={modello} onChange={(e) => setModello(e.target.value)} className="input w-full">
              <option value="gpt-5.2">GPT-5.2 (best)</option>
              <option value="gpt-5.1">GPT-5.1 (raccomandato)</option>
              <option value="gpt-5-mini">GPT-5-mini (veloce)</option>
              <option value="gpt-4o">GPT-4o</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Personalita / System prompt</label>
          <textarea value={personalita} onChange={(e) => setPersonalita(e.target.value)} rows="12"
            className="input w-full font-mono text-xs" data-testid="pers-prompt" />
          <p className="text-[10px] text-slate-400 mt-1">Questo testo definisce il carattere di Geppo. Verra iniettato in ogni conversazione.</p>
        </div>
      </div>
      <button onClick={() => onSave({ nome_assistente: nome, emoji, voce_modello: voce, modello_llm: modello, personalita })}
        disabled={saving} data-testid="save-personalita"
        className="bg-primary text-white text-sm font-bold px-4 py-2 rounded hover:bg-primary/90 disabled:opacity-50">
        {saving ? 'Salvataggio...' : 'Salva personalita'}
      </button>
    </div>
  );
}

